import { useDispatch } from "react-redux";
import { useState, useEffect, useMemo } from "react";
import { auctionPropertiesThunks } from "../../app/store";
import { auctionPropertiesApi } from "../../api/auctionPropertiesApi";
import {
  Button,
  Label,
  Spinner,
  TextInput,
  Textarea,
  Select,
} from "flowbite-react";
import PropertyRegridSnapshotCard from "./shared/PropertyRegridSnapshotCard";
import { FiMapPin, FiSearch } from "react-icons/fi";
import { HiExternalLink } from "react-icons/hi";
import {
  parseMoneyToNumber,
  parseDecimalNumber,
} from "../../utils/numberParser";
import { toast } from "react-toastify";
import {
  fillStep2FromAddress,
  fillStep2FromCoordinates,
} from "../../utils/googleGeocoding";

const YES_NO_UNKNOWN = ["YES", "NO", "UNKNOWN"];
const HOMESTEAD_INVESTOR = ["HOMESTEAD", "INVESTOR", "UNKNOWN"];

function safeParseRules(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function sanitize(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function applyTransform(value, transform = {}) {
  let out = sanitize(value);
  if (!out) return out;

  if (transform.stripChars) {
    const chars = String(transform.stripChars);
    const escaped = chars.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(new RegExp(`[${escaped}]`, "g"), "");
  }

  if (transform.stripNonAlnum) {
    out = out.replace(/[^a-zA-Z0-9]/g, "");
  }

  if (transform.upper) out = out.toUpperCase();
  if (transform.lower) out = out.toLowerCase();
  if (transform.prefix) out = `${transform.prefix}${out}`;
  if (transform.suffix) out = `${out}${transform.suffix}`;

  return out;
}

function buildUrlFromRules(rules, property) {
  if (!rules?.url_template) return null;

  const baseValues = {
    parcel_number: property?.parcel_number,
    alt_key: property?.alt_key,
    property_address: property?.property_address,
    owner_name: property?.owner_name,
    latitude: property?.latitude,
    longitude: property?.longitude,
  };

  const computed = { ...baseValues };
  const transforms = rules?.transforms || {};

  Object.entries(transforms).forEach(([targetToken, config]) => {
    const sourceKey = config?.source || targetToken;
    const raw = baseValues[sourceKey] ?? property?.[sourceKey];
    computed[targetToken] = applyTransform(raw, config);
  });

  const url = String(rules.url_template).replace(/{{\s*([a-zA-Z0-9_]+)\s*}}/g, (_, token) => {
    return encodeURIComponent(sanitize(computed[token] ?? property?.[token]));
  });

  if (!/^https?:\/\//i.test(url)) return null;
  return url;
}

export default function Step2Appraiser({
  propertyId,
  property,
  onSave,
  appraiserLink,
  county,
}) {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [fetchingAddress, setFetchingAddress] = useState(false);
  const [fetchingCoordinates, setFetchingCoordinates] = useState(false);
  const [syncingRegrid, setSyncingRegrid] = useState(false);
  const latestSnapshot =
    property?.regridSnapshots?.[0] || property?.PropertyRegridSnapshots?.[0] || null;

  const rules = useMemo(
    () => safeParseRules(county?.appraiser_rules_json),
    [county?.appraiser_rules_json],
  );

  const dynamicAppraiserUrl = useMemo(
    () => buildUrlFromRules(rules, property),
    [rules, property],
  );

  const appraiserUrl = dynamicAppraiserUrl || appraiserLink?.url || null;

  const initialForm = useMemo(
    () => ({
      owner_name: property?.owner_name || "",
      property_address: property?.property_address || "",
      mailing_address: property?.mailing_address || "",

      min_bid: property?.min_bid ?? "",
      appraisal_value: property?.appraisal_value ?? "",
      land_value: property?.land_value ?? "",
      improvement_value: property?.improvement_value ?? "",

      legal_description: property?.legal_description || "",

      homestead_investor: property?.homestead_investor || "UNKNOWN",
      zoning_code: property?.zoning_code || "",
      zoning_description: property?.zoning_description || "",

      lot_area: property?.lot_area ?? "",
      type_lot: property?.type_lot || "",

      electric: property?.electric || "UNKNOWN",
      water: property?.water || "UNKNOWN",
      sewer: property?.sewer || "UNKNOWN",
      latitude: property?.latitude ?? "",
      longitude: property?.longitude ?? "",
    }),
    [
      property?.owner_name,
      property?.property_address,
      property?.mailing_address,
      property?.min_bid,
      property?.appraisal_value,
      property?.land_value,
      property?.improvement_value,
      property?.legal_description,
      property?.homestead_investor,
      property?.zoning_code,
      property?.zoning_description,
      property?.lot_area,
      property?.type_lot,
      property?.electric,
      property?.water,
      property?.sewer,
      property?.latitude,
      property?.longitude,
    ],
  );

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [propertyId, initialForm]);

  async function handleFillFromCoordinates() {
    if (!form.latitude || !form.longitude) {
      toast?.error?.("Informe latitude e longitude primeiro");
      return;
    }

    setFetchingAddress(true);
    try {
      const result = await fillStep2FromCoordinates(
        form.latitude,
        form.longitude,
      );

      setForm((s) => ({
        ...s,
        property_address: result.property_address || s.property_address,
      }));
    } catch (err) {
      console.error("Erro ao buscar endereco pelas coordenadas:", err);
      toast?.error?.(err.message || "Erro ao buscar endereco");
    } finally {
      setFetchingAddress(false);
    }
  }

  async function handleFillFromAddress() {
    if (!form.property_address?.trim()) {
      toast?.error?.("Informe um endereco primeiro");
      return;
    }

    setFetchingCoordinates(true);
    try {
      const result = await fillStep2FromAddress(form.property_address);

      setForm((s) => ({
        ...s,
        latitude: result.latitude || s.latitude,
        longitude: result.longitude || s.longitude,
      }));
    } catch (err) {
      console.error("Erro ao buscar coordenadas pelo endereco:", err);
      toast?.error?.(err.message || "Erro ao buscar coordenadas");
    } finally {
      setFetchingCoordinates(false);
    }
  }

  async function handleSyncRegrid() {
    if (!propertyId) return;

    setSyncingRegrid(true);
    try {
      await auctionPropertiesApi.syncRegrid(propertyId);
      toast?.success?.("Dados do Regrid sincronizados");
      onSave?.();
    } catch (err) {
      console.error("Falha ao sincronizar com o Regrid:", err);
      toast?.error?.(err.message || "Erro ao sincronizar com o Regrid");
    } finally {
      setSyncingRegrid(false);
    }
  }

  async function save() {
    if (!propertyId) return;
    setSaving(true);
    try {
      await dispatch(
        auctionPropertiesThunks.updateOne({
          id: propertyId,
          payload: {
            owner_name: form.owner_name?.trim() || null,
            property_address: form.property_address?.trim() || null,

            min_bid: parseMoneyToNumber(form.min_bid),
            appraisal_value: parseMoneyToNumber(form.appraisal_value),
            land_value: parseMoneyToNumber(form.land_value),
            improvement_value: parseMoneyToNumber(form.improvement_value),

            legal_description: form.legal_description?.trim() || null,
            mailing_address: form.mailing_address?.trim() || null,

            homestead_investor: form.homestead_investor || "UNKNOWN",
            zoning_code: form.zoning_code?.trim() || null,
            zoning_description:
              form.zoning_description?.trim() || null,

            lot_area: parseDecimalNumber(form.lot_area),
            type_lot: form.type_lot?.trim() || null,

            electric: form.electric || "UNKNOWN",
            water: form.water || "UNKNOWN",
            sewer: form.sewer || "UNKNOWN",
            latitude: parseDecimalNumber(form.latitude),
            longitude: parseDecimalNumber(form.longitude),
          },
        }),
      ).unwrap();

      onSave?.();
    } catch (err) {
      console.error("Falha ao salvar Step 2:", err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        {appraiserUrl && (
          <Button
            className="gap-2"
            color="dark"
            onClick={() =>
              window.open(appraiserUrl, "_blank", "noopener,noreferrer")
            }
          >
            Appraiser <HiExternalLink />
          </Button>
        )}

        <Button
          color="dark"
          onClick={handleSyncRegrid}
          disabled={syncingRegrid || !propertyId}
        >
          {syncingRegrid ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Syncing Regrid Data
            </span>
          ) : (
            "Sync Regrid Data"
          )}
        </Button>
      </div>

      <PropertyRegridSnapshotCard snapshot={latestSnapshot} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Owner Name</Label>
          <TextInput
            value={form.owner_name}
            onChange={(e) =>
              setForm((s) => ({ ...s, owner_name: e.target.value }))
            }
            placeholder="Ex: Maria Johnson"
          />
        </div>

        <div>
          <Label className="mb-1 block">Property Address</Label>
          <div className="relative">
            <TextInput
              value={form.property_address}
              onChange={(e) =>
                setForm((s) => ({ ...s, property_address: e.target.value }))
              }
              placeholder="Ex: 123 Main St, Tampa, FL 33602"
              className="[&>input]:pr-10"
            />

            <button
              type="button"
              onClick={handleFillFromCoordinates}
              title="Buscar coordenadas pelo endereco"
              disabled={fetchingAddress}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiSearch className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="md:col-span-2">
          <Label>Mailing Address</Label>
          <Textarea
            rows={2}
            value={form.mailing_address}
            onChange={(e) =>
              setForm((s) => ({ ...s, mailing_address: e.target.value }))
            }
            placeholder="Endereco dos donos"
          />
        </div>

        <div>
          <Label>Min Bid (USD)</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.min_bid}
            onChange={(e) =>
              setForm((s) => ({ ...s, min_bid: e.target.value }))
            }
            placeholder="Ex: 1500.00"
          />
        </div>

        <div>
          <Label>Appraisal Value (USD)</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.appraisal_value}
            onChange={(e) =>
              setForm((s) => ({ ...s, appraisal_value: e.target.value }))
            }
            placeholder="Ex: 185000.00"
          />
        </div>

        <div>
          <Label>Land Value (USD)</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.land_value}
            onChange={(e) =>
              setForm((s) => ({ ...s, land_value: e.target.value }))
            }
            placeholder="Ex: 192300.00"
          />
        </div>

        <div>
          <Label>Improvement Value (USD)</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.improvement_value}
            onChange={(e) =>
              setForm((s) => ({ ...s, improvement_value: e.target.value }))
            }
            placeholder="Ex: 1043300.00"
          />
        </div>

        <div>
          <Label>Latitude</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            icon={FiMapPin}
            value={form.latitude}
            onChange={(e) =>
              setForm((s) => ({ ...s, latitude: e.target.value }))
            }
            placeholder="Ex: 28.538336"
          />
        </div>

        <div>
          <Label>Longitude</Label>

          <div className="relative">
            <TextInput
              type="text"
              inputMode="decimal"
              icon={FiMapPin}
              value={form.longitude}
              onChange={(e) =>
                setForm((s) => ({ ...s, longitude: e.target.value }))
              }
              placeholder="Ex: -81.379234"
              className="[&>input]:pr-10"
            />

            <button
              type="button"
              onClick={handleFillFromAddress}
              disabled={fetchingCoordinates}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FiSearch className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div>
        <Label>Legal Description</Label>
        <Textarea
          rows={4}
          value={form.legal_description}
          onChange={(e) =>
            setForm((s) => ({ ...s, legal_description: e.target.value }))
          }
          placeholder="Ex: Lot 12, Block B, Sunshine Subdivision, according to plat..."
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Homestead / Investor</Label>
          <Select
            value={form.homestead_investor}
            onChange={(e) =>
              setForm((s) => ({ ...s, homestead_investor: e.target.value }))
            }
          >
            {HOMESTEAD_INVESTOR.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Zoning Code</Label>
          <TextInput
            value={form.zoning_code}
            onChange={(e) =>
              setForm((s) => ({ ...s, zoning_code: e.target.value }))
            }
            placeholder="Ex: R-1 / RM-16 / C-2"
          />
        </div>

        <div className="md:col-span-3">
          <Label>Zoning Description</Label>
          <TextInput
            value={form.zoning_description}
            onChange={(e) =>
              setForm((s) => ({ ...s, zoning_description: e.target.value }))
            }
            placeholder="Ex: Dwelling District Eight"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Lot Area</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.lot_area}
            onChange={(e) =>
              setForm((s) => ({ ...s, lot_area: e.target.value }))
            }
            placeholder="Ex: 5600 (sqft) ou 0.1285 (acres)"
          />
        </div>

        <div>
          <Label>Type Lot</Label>
          <TextInput
            value={form.type_lot}
            onChange={(e) =>
              setForm((s) => ({ ...s, type_lot: e.target.value }))
            }
            placeholder="Ex: Residential / Vacant / Corner lot"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Electric</Label>
          <Select
            value={form.electric}
            onChange={(e) =>
              setForm((s) => ({ ...s, electric: e.target.value }))
            }
          >
            {YES_NO_UNKNOWN.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Water</Label>
          <Select
            value={form.water}
            onChange={(e) => setForm((s) => ({ ...s, water: e.target.value }))}
          >
            {YES_NO_UNKNOWN.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Label>Sewer</Label>
          <Select
            value={form.sewer}
            onChange={(e) => setForm((s) => ({ ...s, sewer: e.target.value }))}
          >
            {YES_NO_UNKNOWN.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button color="gray" onClick={save} disabled={saving}>
          {saving ? "Salvando..." : "Salvar Step 2"}
        </Button>
      </div>
    </div>
  );
}
